
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { pgApi } from '../utils/pgApi';
import { useToast } from '../components/common/ToastProvider';
import ErrorMessage from '../components/common/ErrorMessage';
import LoadingMessage from '../components/common/LoadingMessage';

const STEPS = [
    { id: 'intro', label: 'Welcome' },
    { id: 'location', label: 'Location' },
    { id: 'floors', label: 'Floors' },
    { id: 'rooms', label: 'Rooms' },
    { id: 'isp', label: 'ISP Details' },
    { id: 'router', label: 'Router' },
    { id: 'ap', label: 'Access Point' },
    { id: 'device', label: 'Device' },
    { id: 'finish', label: 'Finish' }
];

const WIZARD_CONTAINER_STYLE = {
    maxWidth: '800px',
    margin: '2rem auto',
    padding: '2rem',
    background: 'var(--color-surface)',
    borderRadius: '12px',
    boxShadow: 'var(--shadow-medium)',
    color: 'var(--color-text)',
};

const BUTTON_GROUP_STYLE = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '2rem',
    paddingTop: '1rem',
    borderTop: '1px solid var(--color-glass-border)',
};

export function SetupWizardPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { addToast } = useToast();
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // State to hold created entity IDs to link them (e.g. room to location)
    const [wizardState, setWizardState] = useState({
        locationId: null,
        locationDescription: '',
        floors: [], // array of { id, floor_name, physical_level }
        rooms: [], // array of { id, room_name }
        ispId: null,
        routerId: null,
        apId: null,
        deviceId: null
    });

    // Form states
    const [locationForm, setLocationForm] = useState({
        // description serves as the name/label in the schema
        description: '',
        address_line1: '',
        city: '',
        country: '',
        house_number: '',
        postcode: '',
        street: '',
        lat: 0,
        lng: 0
    });
    const [floorForm, setFloorForm] = useState({ name: '', level: '0' });
    const [roomForm, setRoomForm] = useState({ name: '', floorId: '' });
    const [ispForm, setIspForm] = useState({ provider: '', type: '' });
    const [routerForm, setRouterForm] = useState({
        mac: '',
        model: '',
        make: '',
        public_ip: '',
        gateway_ip: '',
        modem_type: '',
        firmware_version: '',
        serial_number: '',
        roomId: ''
    });
    const [apForm, setApForm] = useState({ mac: '', band: '50', model: '', roomId: '' });
    const [deviceForm, setDeviceForm] = useState({ mac: '', device_name: '', model: '', roomId: '' });

    const currentStep = STEPS[currentStepIndex];

    const handleNext = () => {
        if (currentStepIndex < STEPS.length - 1) {
            setCurrentStepIndex(currentStepIndex + 1);
            setError(null);
        } else {
            navigate('/');
        }
    };

    const handleBack = () => {
        if (currentStepIndex > 0) {
            setCurrentStepIndex(currentStepIndex - 1);
            setError(null);
        }
    };

    const createLocation = async () => {
        if (!locationForm.description) {
            setError("Location Name/Description is required");
            return;
        }
        if (locationForm.country.length !== 2) {
            setError("Country must be a 2-letter code (e.g., US, UK, DE)");
            return;
        }
        setLoading(true);
        try {
            // 1. Create Location
            const payload = {
                description: locationForm.description,
                house_number: parseInt(locationForm.house_number, 10) || 0,
                street: locationForm.street,
                city: locationForm.city,
                postcode: locationForm.postcode,
                country: locationForm.country.toUpperCase(),
                lat: locationForm.lat,
                lng: locationForm.lng,
                user_id: user.id
            };
            const locResult = await pgApi.createUserLocation(user.id, payload);

            setWizardState(prev => ({
                ...prev,
                locationId: locResult.id,
                locationDescription: locResult.description
            }));

            addToast({ type: 'success', title: 'Location Created', description: locResult.description });
            handleNext();
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to create location");
        } finally {
            setLoading(false);
        }
    };

    const createFloor = async () => {
        if (!floorForm.name) return;
        setLoading(true);
        try {
            const payload = {
                floor_name: floorForm.name,
                physical_level: parseInt(floorForm.level, 10),
                location_id: wizardState.locationId
            };
            const result = await pgApi.createUserFloor(user.id, payload);
            setWizardState(prev => ({ ...prev, floors: [...prev.floors, result] }));
            setFloorForm({ name: '', level: '0' }); // clear input
            addToast({ type: 'success', title: 'Floor Added', description: result.floor_name });
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to create floor");
        } finally {
            setLoading(false);
        }
    };

    const createRoom = async () => {
        if (!roomForm.name || !roomForm.floorId) return;

        setLoading(true);
        try {
            const payload = {
                room_name: roomForm.name,
                location_floor_id: roomForm.floorId
            };
            const result = await pgApi.createUserRoom(user.id, payload);
            setWizardState(prev => ({ ...prev, rooms: [...prev.rooms, result] }));
            setRoomForm(prev => ({ ...prev, name: '' })); // clear name but keep floor selection
            addToast({ type: 'success', title: 'Room Added', description: result.room_name });
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to create room");
        } finally {
            setLoading(false);
        }
    };

    const createIsp = async () => {
        if (!ispForm.provider) {
            setError("ISP Provider Name is required");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                provider: ispForm.provider,
                type: ispForm.type,
                user_id: user.id
            };
            const result = await pgApi.createUserIspDetail(user.id, payload);
            setWizardState(prev => ({ ...prev, ispId: result.id }));
            addToast({ type: 'success', title: 'ISP Added', description: result.provider });
            handleNext();
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to create ISP detail");
        } finally {
            setLoading(false);
        }
    };

    const createRouter = async () => {
        if (!routerForm.mac) {
            setError("MAC address is required");
            return;
        }
        if (!wizardState.ispId) {
            setError("ISP details are missing. Please go back and add ISP.");
            return;
        }
        if (!routerForm.roomId) {
            setError("Please select a room for the router.");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                mac: routerForm.mac,
                make: routerForm.make,
                model: routerForm.model,
                public_ip: routerForm.public_ip,
                gateway_ip: routerForm.gateway_ip,
                modem_type: routerForm.modem_type,
                firmware_version: routerForm.firmware_version,
                serial_number: routerForm.serial_number,

                isp_id: wizardState.ispId,
                location_room_id: routerForm.roomId,
                location_id: wizardState.locationId,
                user_id: user.id
            };

            const result = await pgApi.createUserRouter(user.id, payload);
            setWizardState(prev => ({ ...prev, routerId: result.id }));
            addToast({ type: 'success', title: 'Router Added', description: result.mac });
            handleNext();
        } catch (err) {
            setError(err.message || "Failed to add router");
        } finally {
            setLoading(false);
        }
    };

    const createAp = async () => {
        if (!apForm.mac) {
            setError("MAC address is required");
            return;
        }
        if (!wizardState.routerId) {
            setError("No Router found. Please go back and add a Router first.");
            return;
        }
        if (!apForm.roomId) {
            setError("Please select a Room location for this Access Point.");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                mac: apForm.mac,
                band: apForm.band,
                model: apForm.model,
                location_id: wizardState.locationId,
                location_room_id: apForm.roomId,
                router_id: wizardState.routerId,
                user_id: user.id
            };
            const result = await pgApi.createUserAccessPoint(user.id, payload);
            setWizardState(prev => ({ ...prev, apId: result.id }));
            addToast({ type: 'success', title: 'Access Point Added', description: result.mac });
            handleNext();
        } catch (err) {
            setError(err.message || "Failed to add Access Point");
        } finally {
            setLoading(false);
        }
    };

    const createDevice = async () => {
        if (!deviceForm.mac) {
            setError("MAC address is required");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                mac: deviceForm.mac,
                device_name: deviceForm.device_name,
                model: deviceForm.model,
                location_room_id: deviceForm.roomId || null, // Optional in schema? 'on delete set null' implies strictly linked but maybe nullable? Schema says 'location_room_id integer references...', doesn't say NOT NULL.
                user_id: user.id
            };
            const result = await pgApi.createUserDevice(user.id, payload);
            setWizardState(prev => ({ ...prev, deviceId: result.id }));
            addToast({ type: 'success', title: 'Device Added', description: result.device_name || result.mac });
            handleNext();
        } catch (err) {
            setError(err.message || "Failed to add device");
        } finally {
            setLoading(false);
        }
    };


    const renderIntro = () => (
        <div>
            <h2>Welcome to Wifi Buddy Setup</h2>
            <p>This wizard will guide you through setting up your network environment.</p>
            <p>We'll help you:</p>
            <ul>
                <li>Create a Location (e.g., your home)</li>
                <li>Add Rooms</li>
                <li>Register your Routers and Access Points</li>
                <li>Add your key Devices</li>
            </ul>
        </div>
    );

    const renderLocationParams = () => (
        <div className="form-group">
            <h3>Step {currentStepIndex + 1}: Create Location</h3>
            <div className="form-field">
                <label className="form-label">Name / Description</label>
                <input
                    value={locationForm.description}
                    onChange={e => setLocationForm({ ...locationForm, description: e.target.value })}
                    placeholder="e.g. My Apartment, Main Office"
                />
            </div>

            <div className="form-field--horizontal">
                <div className="form-field" style={{ flex: 1 }}>
                    <label className="form-label">House Number</label>
                    <input
                        type="number"
                        value={locationForm.house_number}
                        onChange={e => setLocationForm({ ...locationForm, house_number: e.target.value })}
                        placeholder="#"
                    />
                </div>
                <div className="form-field" style={{ flex: 3 }}>
                    <label className="form-label">Street</label>
                    <input
                        value={locationForm.street}
                        onChange={e => setLocationForm({ ...locationForm, street: e.target.value })}
                        placeholder="Street Name"
                    />
                </div>
            </div>

            <div className="form-field--horizontal">
                <div className="form-field" style={{ flex: 2 }}>
                    <label className="form-label">City</label>
                    <input
                        value={locationForm.city}
                        onChange={e => setLocationForm({ ...locationForm, city: e.target.value })}
                        placeholder="City"
                    />
                </div>
                <div className="form-field" style={{ flex: 1 }}>
                    <label className="form-label">Postcode</label>
                    <input
                        value={locationForm.postcode}
                        onChange={e => setLocationForm({ ...locationForm, postcode: e.target.value })}
                        placeholder="Zip/Post Code"
                    />
                </div>
            </div>

            <div className="form-field">
                <label className="form-label">Country (2-letter Code)</label>
                <input
                    value={locationForm.country}
                    onChange={e => setLocationForm({ ...locationForm, country: e.target.value.toUpperCase().slice(0, 2) })}
                    placeholder="US, UK, DE, etc."
                    maxLength={2}
                />
            </div>
        </div>
    );

    const renderFloorParams = () => (
        <div className="form-group">
            <h3>Step {currentStepIndex + 1}: Add Floors to {wizardState.locationDescription}</h3>

            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'flex-end' }}>
                <div className="form-field" style={{ flex: 2, marginBottom: 0 }}>
                    <label className="form-label">Floor Name</label>
                    <input
                        value={floorForm.name}
                        onChange={e => setFloorForm({ ...floorForm, name: e.target.value })}
                        placeholder="e.g. Ground Floor, 1st Floor"
                    />
                </div>
                <div className="form-field" style={{ flex: 1, marginBottom: 0 }}>
                    <label className="form-label">Level Number</label>
                    <input
                        type="number"
                        value={floorForm.level}
                        onChange={e => setFloorForm({ ...floorForm, level: e.target.value })}
                        placeholder="0"
                    />
                </div>
                <button className="primary-btn" onClick={createFloor} disabled={!floorForm.name || loading}>Add</button>
            </div>

            {wizardState.floors.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                    <h4>Floors Added:</h4>
                    <ul>
                        {wizardState.floors.map(f => <li key={f.id}>{f.floor_name} (Level {f.physical_level})</li>)}
                    </ul>
                </div>
            )}
            <p className="form-helper-text" style={{ marginTop: '1rem' }}>
                Add at least one floor (e.g., "Ground Floor") to proceed.
            </p>
        </div>
    );

    const renderRoomParams = () => (
        <div className="form-group">
            <h3>Step {currentStepIndex + 1}: Add Rooms</h3>

            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div className="form-field" style={{ flex: 1, minWidth: '200px', marginBottom: 0 }}>
                    <label className="form-label">Select Floor</label>
                    <select
                        value={roomForm.floorId}
                        onChange={e => setRoomForm({ ...roomForm, floorId: e.target.value })}
                    >
                        <option value="">-- Choose Floor --</option>
                        {wizardState.floors.map(f => (
                            <option key={f.id} value={f.id}>{f.floor_name}</option>
                        ))}
                    </select>
                </div>

                <div className="form-field" style={{ flex: 2, minWidth: '200px', marginBottom: 0 }}>
                    <label className="form-label">Room Name</label>
                    <input
                        value={roomForm.name}
                        onChange={e => setRoomForm({ ...roomForm, name: e.target.value })}
                        placeholder="e.g. Living Room, Kitchen"
                    />
                </div>
                <button
                    className="primary-btn"
                    onClick={createRoom}
                    disabled={!roomForm.name || !roomForm.floorId || loading}
                >
                    Add
                </button>
            </div>

            {wizardState.rooms.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                    <h4>Rooms Added:</h4>
                    <ul>
                        {wizardState.rooms.map(r => {
                            const floor = wizardState.floors.find(f => f.id === r.location_floor_id);
                            return <li key={r.id}>{r.room_name} ({floor ? floor.floor_name : 'Unknown Floor'})</li>
                        })}
                    </ul>
                </div>
            )}
        </div>
    );

    const renderIspParams = () => (
        <div className="form-group">
            <h3>Step {currentStepIndex + 1}: ISP Details</h3>
            <div className="form-field">
                <label className="form-label">Provider Name</label>
                <input
                    value={ispForm.provider}
                    onChange={e => setIspForm({ ...ispForm, provider: e.target.value })}
                    placeholder="e.g. Comcast, AT&T, BT"
                />
            </div>
            <div className="form-field">
                <label className="form-label">Type (Optional)</label>
                <select
                    value={ispForm.type}
                    onChange={e => setIspForm({ ...ispForm, type: e.target.value })}
                >
                    <option value="">-- Select Type --</option>
                    <option value="fiber">Fiber</option>
                    <option value="cable">Cable</option>
                    <option value="vdsl">VDSL</option>
                </select>
            </div>
        </div>
    );

    const renderRouterParams = () => (
        <div className="form-group">
            <h3>Step {currentStepIndex + 1}: Add Router</h3>

            <div className="form-field">
                <label className="form-label">Room Location <span style={{ color: 'var(--color-primary)' }}>*</span></label>
                <select
                    value={routerForm.roomId}
                    onChange={e => setRouterForm({ ...routerForm, roomId: e.target.value })}
                >
                    <option value="">-- Select Room --</option>
                    {wizardState.rooms.map(r => (
                        <option key={r.id} value={r.id}>{r.room_name}</option>
                    ))}
                </select>
                {!routerForm.roomId && wizardState.rooms.length === 0 && (
                    <small style={{ color: 'orange' }}>Warning: No rooms created. You should go back and add a room.</small>
                )}
            </div>

            <div className="form-field--horizontal">
                <div className="form-field" style={{ flex: 1 }}>
                    <label className="form-label">MAC Address <span style={{ color: 'var(--color-primary)' }}>*</span></label>
                    <input
                        value={routerForm.mac}
                        onChange={e => setRouterForm({ ...routerForm, mac: e.target.value })}
                        placeholder="00:00:00:00:00:00"
                    />
                </div>
                <div className="form-field" style={{ flex: 1 }}>
                    <label className="form-label">Serial Number (Optional)</label>
                    <input
                        value={routerForm.serial_number}
                        onChange={e => setRouterForm({ ...routerForm, serial_number: e.target.value })}
                        placeholder="Serial #"
                    />
                </div>
            </div>

            <div className="form-field--horizontal">
                <div className="form-field" style={{ flex: 1 }}>
                    <label className="form-label">Make/Brand</label>
                    <input
                        value={routerForm.make}
                        onChange={e => setRouterForm({ ...routerForm, make: e.target.value })}
                        placeholder="e.g. Netgear"
                    />
                </div>
                <div className="form-field" style={{ flex: 1 }}>
                    <label className="form-label">Model</label>
                    <input
                        value={routerForm.model}
                        onChange={e => setRouterForm({ ...routerForm, model: e.target.value })}
                        placeholder="e.g. Nighthawk"
                    />
                </div>
            </div>

            <div className="form-field--horizontal">
                <div className="form-field" style={{ flex: 1 }}>
                    <label className="form-label">Public IP (Optional)</label>
                    <input
                        value={routerForm.public_ip}
                        onChange={e => setRouterForm({ ...routerForm, public_ip: e.target.value })}
                        placeholder="x.x.x.x"
                    />
                </div>
                <div className="form-field" style={{ flex: 1 }}>
                    <label className="form-label">Gateway IP (Optional)</label>
                    <input
                        value={routerForm.gateway_ip}
                        onChange={e => setRouterForm({ ...routerForm, gateway_ip: e.target.value })}
                        placeholder="192.168.1.1"
                    />
                </div>
            </div>

            <div className="form-field--horizontal">
                <div className="form-field" style={{ flex: 1 }}>
                    <label className="form-label">Modem Type (Optional)</label>
                    <input
                        value={routerForm.modem_type}
                        onChange={e => setRouterForm({ ...routerForm, modem_type: e.target.value })}
                        placeholder="e.g. DOCSIS 3.1"
                    />
                </div>
                <div className="form-field" style={{ flex: 1 }}>
                    <label className="form-label">Firmware Ver. (Optional)</label>
                    <input
                        value={routerForm.firmware_version}
                        onChange={e => setRouterForm({ ...routerForm, firmware_version: e.target.value })}
                        placeholder="v1.0.0"
                    />
                </div>
            </div>

        </div>
    );

    const renderApParams = () => (
        <div className="form-group">
            <h3>Step {currentStepIndex + 1}: Add Access Point</h3>

            <div className="form-field">
                <label className="form-label">Room Location <span style={{ color: 'var(--color-primary)' }}>*</span></label>
                <select
                    value={apForm.roomId}
                    onChange={e => setApForm({ ...apForm, roomId: e.target.value })}
                >
                    <option value="">-- Select Room --</option>
                    {wizardState.rooms.map(r => (
                        <option key={r.id} value={r.id}>{r.room_name}</option>
                    ))}
                </select>
            </div>

            <div className="form-field">
                <label className="form-label">BSSID (MAC)</label>
                <input
                    value={apForm.mac}
                    onChange={e => setApForm({ ...apForm, mac: e.target.value })}
                    placeholder="00:00:00:00:00:00"
                />
            </div>
            <div className="form-field">
                <label className="form-label">Frequency Band</label>
                <select
                    value={apForm.band}
                    onChange={e => setApForm({ ...apForm, band: e.target.value })}
                >
                    <option value="24">2.4 GHz</option>
                    <option value="50">5 GHz</option>
                </select>
            </div>

            <div className="form-field">
                <label className="form-label">Model</label>
                <input
                    value={apForm.model}
                    onChange={e => setApForm({ ...apForm, model: e.target.value })}
                    placeholder="e.g. TP-Link EAP"
                />
            </div>
        </div>
    );

    const renderDeviceParams = () => (
        <div className="form-group">
            <h3>Step {currentStepIndex + 1}: Add Client Device</h3>

            <div className="form-field">
                <label className="form-label">Room Location (Optional)</label>
                <select
                    value={deviceForm.roomId}
                    onChange={e => setDeviceForm({ ...deviceForm, roomId: e.target.value })}
                >
                    <option value="">-- Select Room --</option>
                    {wizardState.rooms.map(r => (
                        <option key={r.id} value={r.id}>{r.room_name}</option>
                    ))}
                </select>
            </div>

            <div className="form-field">
                <label className="form-label">MAC Address</label>
                <input
                    value={deviceForm.mac}
                    onChange={e => setDeviceForm({ ...deviceForm, mac: e.target.value })}
                    placeholder="00:00:00:00:00:00"
                />
            </div>
            <div className="form-field">
                <label className="form-label">Device Name</label>
                <input
                    value={deviceForm.device_name}
                    onChange={e => setDeviceForm({ ...deviceForm, device_name: e.target.value })}
                    placeholder="e.g. My Phone"
                />
            </div>
            <div className="form-field">
                <label className="form-label">Model</label>
                <input
                    value={deviceForm.model}
                    onChange={e => setDeviceForm({ ...deviceForm, model: e.target.value })}
                    placeholder="e.g. iPhone 13"
                />
            </div>
        </div>
    );

    const renderFinish = () => (
        <div>
            <h2>Setup Complete!</h2>
            <p>You have successfully configured your initial environment.</p>
            <p>You can verify your setup on the Home Dashboard.</p>
        </div>
    );


    const renderStepContent = () => {
        switch (currentStep.id) {
            case 'intro': return renderIntro();
            case 'location': return renderLocationParams();
            case 'floors': return renderFloorParams();
            case 'rooms': return renderRoomParams();
            case 'isp': return renderIspParams();
            case 'router': return renderRouterParams();
            case 'ap': return renderApParams();
            case 'device': return renderDeviceParams();
            case 'finish': return renderFinish();
            default: return <div>Unknown Step</div>;
        }
    };

    const handleCustomNext = () => {
        // Special handlers for steps that require API calls before processing
        if (currentStep.id === 'location') {
            createLocation();
        } else if (currentStep.id === 'isp') {
            createIsp();
        } else if (currentStep.id === 'router') {
            createRouter();
        } else if (currentStep.id === 'ap') {
            createAp();
        } else if (currentStep.id === 'device') {
            createDevice();
        } else {
            handleNext();
        }
    };

    return (
        <div style={WIZARD_CONTAINER_STYLE}>
            {error && <ErrorMessage message={error} />}
            {loading && <LoadingMessage message="Processing..." />}

            <div style={{ marginBottom: '2rem' }}>
                Step {currentStepIndex + 1} of {STEPS.length}: {currentStep.label}
                <div style={{ height: '4px', background: 'var(--color-glass-border)', marginTop: '0.5rem', borderRadius: '2px' }}>
                    <div style={{
                        height: '100%',
                        width: `${((currentStepIndex + 1) / STEPS.length) * 100}%`,
                        background: 'var(--color-primary)',
                        borderRadius: '2px',
                        transition: 'width 0.3s ease'
                    }}></div>
                </div>
            </div>

            {renderStepContent()}

            <div style={BUTTON_GROUP_STYLE}>
                <button
                    className="secondary-btn"
                    onClick={handleBack}
                    disabled={currentStepIndex === 0 || loading || currentStep.id === 'finish'}
                >
                    Back
                </button>

                {currentStep.id === 'finish' ? (
                    <button className="primary-btn" onClick={() => navigate('/')}>
                        Go to Dashboard
                    </button>
                ) : (
                    <>
                        {/* Option to skip some steps? */}
                        {(currentStep.id === 'router' || currentStep.id === 'ap' || currentStep.id === 'device') && (
                            <button className="secondary-btn" onClick={handleNext} disabled={loading} style={{ marginRight: 'auto', marginLeft: '1rem' }}>
                                Skip this step
                            </button>
                        )}

                        <button className="primary-btn" onClick={handleCustomNext} disabled={loading}>
                            {currentStepIndex === STEPS.length - 1 ? 'Finish' : 'Next'}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

export default SetupWizardPage;
